/**
 * Subscription - 充值优惠 Tab
 * 展示充值赠送选项，点击跳转至我的钱包
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import { Gift, Sparkles, ArrowRight, Zap } from 'lucide-react';
import Button from '../../../components/ui/Button';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface RechargeOption {
  amount: number;
  bonus: number;
  popular: boolean;
}

export default function Subscription() {
  const { user } = useAuth();
  const [options, setOptions] = useState<RechargeOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/config/recharge`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.data) && data.data.length > 0) {
          setOptions(data.data);
        } else {
          setOptions([
            { amount: 29, bonus: 2, popular: false },
            { amount: 99, bonus: 15, popular: true },
            { amount: 299, bonus: 60, popular: false },
          ]);
        }
      })
      .catch(() => {
        setOptions([
          { amount: 29, bonus: 2, popular: false },
          { amount: 99, bonus: 15, popular: true },
          { amount: 299, bonus: 60, popular: false },
        ]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleRecharge = () => {
    window.location.href = '/profile?tab=wallet';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 余额卡片 */}
      <div className="bg-gradient-to-r from-indigo-500 to-violet-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-indigo-100 text-sm mb-1">当前余额</p>
            <p className="text-3xl font-bold">¥{user?.balance?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
            <Zap className="w-7 h-7 text-white" />
          </div>
        </div>
      </div>

      {/* 充值优惠 */}
      <div>
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Gift className="w-5 h-5 text-indigo-600" />
          充值优惠
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {options.map((opt, index) => (
            <motion.div
              key={opt.amount}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-2xl border-2 p-5 transition-all ${
                opt.popular
                  ? 'border-amber-300 bg-amber-50'
                  : 'border-slate-200 bg-white hover:border-indigo-200'
              }`}
            >
              {opt.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  最受欢迎
                </div>
              )}
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-800 mb-1">
                  ¥{opt.amount}
                </p>
                {opt.bonus > 0 && (
                  <p className="text-sm text-emerald-600 font-medium mb-3 flex items-center justify-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    赠送 ¥{opt.bonus}
                  </p>
                )}
                <p className="text-xs text-slate-400">
                  实得 ¥{opt.amount + opt.bonus}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* 去充值按钮 */}
      <div className="text-center">
        <Button
          onClick={handleRecharge}
          size="lg"
          className="bg-indigo-600 text-white rounded-xl px-8 hover:bg-indigo-700"
        >
          去充值
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        <p className="text-sm text-slate-400 mt-3">
          充值越多赠送越多，余额可用于所有创作消费
        </p>
      </div>
    </div>
  );
}
